import type { LandingContent } from "./types";

export const en: LandingContent = {
  locale: "en",
  seo: {
    title: "Prismark Pro Camera · Lightweight Camera Tools for iOS",
    description:
      "Prismark is a lightweight pro camera app for iOS with manual shutter and ISO controls, live histogram and zebra warnings, custom LUT import, dual-camera PiP recording, and HDR video."
  },
  nav: {
    features: "Features",
    pro: "Prismark Pro",
    dualCamera: "Dual Camera",
    screens: "Screens",
    download: "Download",
    privacy: "Privacy Policy",
    support: "Support",
    language: "中文"
  },
  hero: {
    tag: "iOS Camera Tool",
    title: "Prismark",
    headline: "Focused viewfinder, intuitive manual controls.",
    body:
      "Fine-tune EV, shutter speed, ISO, focus, and white balance with a live histogram and zebra overexposure warnings. Built on native iOS camera frameworks.",
    primaryCta: "Download",
    secondaryCta: "Explore Features"
  },
  overview: {
    title: "Core Tools for Daily Shooting",
    subtitle:
      "Capture photos, record video, shoot dual-camera footage, and inspect results with direct manual controls and real-time exposure aids.",
    features: [
      { title: "Photo Capture", body: "Responsive viewfinder and shutter with manual controls for EV, shutter speed, ISO, focus, and white balance." },
      { title: "Video Recording", body: "Record at 4K and 60 fps with 10-bit HDR support on compatible devices and clear status monitoring." },
      { title: "Dual Camera", body: "Record front and back camera views at the same time with an interactive picture-in-picture window.", badge: "Prismark Pro" },
      { title: "In-Camera Review", body: "Inspect shots immediately with full EXIF parameters, color profiles, resolution data, and quick playback." }
    ]
  },
  camera: {
    title: "Viewfinder and Manual Parameters",
    body:
      "Adjust EV, shutter speed, ISO, focus, and white balance directly from the toolbar. A live luminance histogram and zebra pattern warning let you evaluate exposure before pressing the shutter.",
    points: ["Grid lines and level meter", "Live luminance histogram", "Zebra overexposure warnings", "Tap to focus with AE/AF lock", "Built-in filters with adjustable intensity", "Parameter toolbar opens on demand"]
  },
  video: {
    title: "Stable and High-Quality Video",
    body:
      "Video mode keeps parameters visible and composition lines active. Supported devices can capture 10-bit HDR video with clear elapsed-time counters and status indicators.",
    points: ["10-bit HDR video recording on supported devices", "Live status indicator and elapsed timer", "Quick zoom presets for standard focal lengths", "Video torch and lighting control"]
  },
  dual: {
    title: "Simultaneous Dual-Camera Recording",
    body:
      "On supported hardware, record the scene and your reaction simultaneously. Drag the picture-in-picture window anywhere on screen. Useful for vlogs, walkthroughs, reviews, and travel stories.",
    points: ["Simultaneous capture of scene and reaction", "Front and back cameras active together", "Movable picture-in-picture window", "Single consolidated 10-bit HDR export"],
    badge: "Prismark Pro"
  },
  gallery: {
    title: "Fast In-Camera Review",
    body:
      "Tap the thumbnail to view recent captures, complete EXIF metadata, lens focal lengths, and resolution details without leaving the camera."
  },
  pro: {
    eyebrow: "One-time purchase",
    title: "Prismark Pro",
    body:
      "Three advanced tools belong to Prismark Pro, unlocked permanently with a single purchase. Everything else in Prismark is included, with nothing further to pay. It is not a subscription: no recurring charges, no renewal cycles, and nothing to cancel.",
    items: [
      { title: "Import Custom LUTs", body: "Load .cube and Hald PNG files alongside the built-in filters, with the same fine-grained intensity slider." },
      { title: "ProRAW and RAW Output", body: "Save raw sensor data on supported models for maximum dynamic range and color latitude in post-processing." },
      { title: "Dual-Camera PiP", body: "Record both cameras in a single 10-bit HDR clip that preserves both your perspective and the scene." }
    ],
    note:
      "Nothing else costs extra: manual shutter and ISO controls, built-in filters, 4K 60 fps recording, HDR video, photo watermarks, and composition aids. If you downloaded Prismark before Prismark Pro was introduced, all three features remain unlocked for you."
  },
  screens: {
    title: "Clean Interface Across Modes",
    subtitle: "From taking photos and recording video to dual-camera capture and reviewing shots, every tool stays accessible within a unified viewfinder.",
    labels: ["Photo", "Video", "Dual", "Gallery"]
  },
  philosophy: {
    title: "Interface and Interaction Details",
    cards: [
      { title: "Native Architecture", body: "Built directly on native iOS camera frameworks for instant startup and stable performance, free from third-party analytics or ads." },
      { title: "Controls on Demand", body: "The viewfinder stays uncluttered while framing. Tap any parameter to open its adjustment slider without obstructing the composition." },
      { title: "System-Level Feel", body: "Supports light and dark appearances, responsive haptic feedback, and familiar pinch-to-zoom gestures that fit naturally into iOS." }
    ]
  },
  cta: {
    title: "Get Prismark on the App Store",
    body: "Available for iPhone and iPad. Prismark Pro is an optional one-time purchase; nothing else in the app costs extra.",
    label: "Download on the App Store"
  },
  footer: {
    tagline: "A lightweight pro camera for iOS.",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    support: "Support",
    contact: "Contact",
    productBy: "A product by Axqora.",
    icp: "粤ICP备2021054614号",
    icpHref: "https://beian.miit.gov.cn/"
  }
};
